import { useCallback, useEffect, useRef, useState } from "react";

import { fetchAdminTenantsPage } from "../../../api/adminData/tenants";

/**
 * The "Ohne Mietverhältnis" block's own data source.
 *
 * A SECOND, deliberately separate query beside the page's tenancy list. The
 * two are never merged, and that is the point rather than a shortcut: the
 * tenancy list is ordered by `move_in_date DESC, id` and this one by
 * `Tenant.name`, so there is no ordering under which a single offset/limit
 * could page through both. Merging them client-side would produce a page
 * boundary that means nothing and a `total` that describes neither set.
 *
 * `enabled` is how the block obeys the toolbar. A tenancy status and a unit
 * are properties of a CONTRACT; for a person who holds none they are not
 * false, they are undefined. So when either filter is set the block is not
 * filtered — it is absent, and no request goes out at all.
 *
 * Its own `skip` lives here and nowhere else. Nothing in this hook can move
 * the tenancy list's page, which is the one coupling a shared pager would
 * have introduced.
 */

/** Matches the overview's own default page size. */
export const UNASSIGNED_PAGE_SIZE = 25;

const EMPTY = { loading: false, error: null, items: [], total: 0 };

export default function useUnassignedTenants({
  search,
  enabled,
  pageSize = UNASSIGNED_PAGE_SIZE,
}) {
  const [skip, setSkip] = useState(0);
  const [state, setState] = useState(EMPTY);

  // Guards a late response from a superseded query overwriting a newer one —
  // the same shape the page uses for its tenancy rows.
  const requestSeq = useRef(0);

  // Any change to what is being asked for starts again at the first page.
  // Mirrors the page's own `setPage(0)` effect; React bails out when the value
  // is already 0, so the common case costs no extra render and no extra fetch.
  useEffect(() => {
    setSkip(0);
  }, [search, enabled, pageSize]);

  const load = useCallback(() => {
    if (!enabled) {
      // Clear rather than freeze: a stale block must not flash back when the
      // filter is reset, and `total` must not survive as a count of something
      // nobody is looking at.
      requestSeq.current += 1;
      setState(EMPTY);
      return Promise.resolve();
    }

    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    return fetchAdminTenantsPage({
      q: search || undefined,
      without_tenancy: true,
      offset: skip,
      limit: pageSize,
    })
      .then((res) => {
        if (requestSeq.current !== seq) return;
        const nextItems = Array.isArray(res?.items) ? res.items : [];
        const nextTotal = Number(res?.total || 0);
        setState({
          loading: false,
          error: null,
          items: nextItems,
          total: nextTotal,
        });
        // Deleting the last person on page 2 leaves the offset pointing past
        // the end: an empty page under a non-zero total, and the block would
        // render nothing while claiming rows exist. Step back to the start
        // rather than guessing which page they moved to.
        if (nextItems.length === 0 && nextTotal > 0 && skip > 0) setSkip(0);
      })
      .catch((e) => {
        if (requestSeq.current !== seq) return;
        setState({
          loading: false,
          error:
            e?.message || "Mieter ohne Mietverhältnis konnten nicht geladen werden.",
          items: [],
          total: 0,
        });
      });
  }, [enabled, search, skip, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    ...state,
    skip,
    pageSize,
    setSkip,
    /** Re-runs the current query — used after a tenant is created or deleted. */
    reload: load,
  };
}
